import { useEffect, useState } from "react";
import { api } from "@/api";
import { ROLE_LABELS, fmtTime } from "@/lib/constants";
import { ScrollText, Search, Download } from "lucide-react";
import { jsPDF } from "jspdf";

const ACTION_COLORS = {
  aangemaakt: "bg-emerald-50 text-emerald-700",
  gewijzigd: "bg-blue-50 text-blue-700",
  verwijderd: "bg-red-50 text-red-700",
  gesloten: "bg-slate-100 text-slate-700",
};

const TYPE_FILTERS = [
  { key: "alle", label: "Alles" },
  { key: "login", label: "Login / toegang" },
  { key: "wijziging", label: "Wijzigingen" },
  { key: "apparaat", label: "Apparaten" },
];
const catOf = (l) => (l.entity === "toegang" ? "login" : (["apparaat", "koppelcode"].includes(l.entity) ? "apparaat" : "wijziging"));

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("alle");
  const [userFilter, setUserFilter] = useState("alle");

  useEffect(() => { api.get("/audit").then(({ data }) => setLogs(data)); }, []);

  const users = [...new Set(logs.map((l) => l.user_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const filtered = logs.filter((l) =>
    (typeFilter === "alle" || catOf(l) === typeFilter) &&
    (userFilter === "alle" || l.user_name === userFilter) &&
    (!q || [l.user_name, l.action, l.entity, l.details, l.ip, l.user_agent].join(" ").toLowerCase().includes(q.toLowerCase()))
  );

  const exportCsv = () => {
    const head = ["Tijd", "Gebruiker", "Rol", "Actie", "Entiteit", "Details", "IP", "Apparaat"];
    const rows = filtered.map((l) => [fmtTime(l.created_at), l.user_name || "", ROLE_LABELS[l.user_role] || l.user_role || "", l.action || "", l.entity || "", (l.details || "").replace(/\n/g, " "), l.ip || "", l.user_agent || ""]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [head, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `logboek_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14); doc.text("DispatchMe — Logboek & audit", 40, 40);
    doc.setFontSize(9);
    let y = 62;
    doc.text(`Geexporteerd: ${new Date().toLocaleString("nl-NL")} · ${filtered.length} regels`, 40, y); y += 18;
    filtered.forEach((l) => {
      if (y > 540) { doc.addPage(); y = 40; }
      const line = `${fmtTime(l.created_at)} | ${l.user_name || ""} (${ROLE_LABELS[l.user_role] || l.user_role || ""}) | ${l.action || ""} | ${l.entity || ""} | ${(l.details || "").replace(/\n/g, " ")} | ${l.ip || ""}`;
      const wrapped = doc.splitTextToSize(line, 760);
      doc.text(wrapped, 40, y); y += 14 * wrapped.length;
    });
    doc.save(`logboek_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-4" data-testid="audit-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Logboek &amp; audit</h1>
          <p className="text-sm text-slate-500">{filtered.length} van {logs.length} regels · login, wijzigingen &amp; apparaten</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoeken…"
            className="rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {TYPE_FILTERS.map((t) => (
          <button key={t.key} data-testid={`audit-filter-${t.key}`} onClick={() => setTypeFilter(t.key)}
            className={`rounded-xl px-3 py-1.5 text-sm font-medium transition-colors ${typeFilter === t.key ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="audit-filter-user" value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-600 outline-none focus:border-blue-500">
            <option value="alle">Alle gebruikers</option>
            {users.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <button data-testid="audit-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> CSV</button>
          <button data-testid="audit-export-pdf" onClick={exportPdf} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> PDF</button>
        </div>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Tijd</th><th className="px-4 py-3">Gebruiker</th><th className="px-4 py-3">Actie</th><th className="px-4 py-3">Entiteit</th><th className="hidden px-4 py-3 md:table-cell">Details</th><th className="hidden px-4 py-3 lg:table-cell">IP-adres</th><th className="hidden px-4 py-3 xl:table-cell">Apparaat</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><ScrollText className="mx-auto mb-2 h-6 w-6" />Geen logregels</td></tr>}
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{fmtTime(l.created_at)}</td>
                <td className="px-4 py-3"><div className="font-medium text-slate-900">{l.user_name}</div><div className="text-xs text-slate-400">{ROLE_LABELS[l.user_role]}</div></td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-600"}`}>{l.action}</span></td>
                <td className="px-4 py-3 text-slate-600">{l.entity}</td>
                <td className="hidden px-4 py-3 text-slate-500 md:table-cell">{l.details}</td>
                <td className="hidden px-4 py-3 font-mono text-xs text-slate-500 lg:table-cell">{l.ip || "—"}</td>
                <td className="hidden max-w-[220px] truncate px-4 py-3 text-xs text-slate-400 xl:table-cell" title={l.user_agent || ""}>{l.user_agent || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
